import { Lightbulb, Pencil, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { DataTable } from "@/components/shared/data-table";
import { EmptyState } from "@/components/shared/empty-state";
import { EnumSelect } from "@/components/shared/enum-select";
import { ErrorState } from "@/components/shared/error-state";
import { CursorPagination } from "@/components/shared/pagination";
import { RowActions } from "@/components/shared/row-actions";
import { SearchInput } from "@/components/shared/search-input";
import { StatusBadge } from "@/components/shared/status-badge";
import { Button } from "@/components/ui/button";
import {
  type ActivitySuggestion,
  useActivitySuggestions,
  useDeleteActivitySuggestion,
} from "@/hooks/use-activity-suggestions";
import { DEFAULT_LIMIT, useDataGrid } from "@/hooks/use-data-grid";
import { ACTIVITY_CATEGORY_OPTIONS, RECOMMENDATION_SOURCE_OPTIONS } from "@/lib/enums";
import { formatDate } from "@/lib/format";
import { ActivitySuggestionCreateDialog } from "./ActivitySuggestionCreateDialog";
import { ActivitySuggestionEditDialog } from "./ActivitySuggestionEditDialog";

function attachedEntryLabel(suggestion: ActivitySuggestion): string {
  if (!suggestion.entryId || !suggestion.entryStartDate) return "—";
  return suggestion.entryStartDate === suggestion.entryEndDate
    ? formatDate(suggestion.entryStartDate)
    : `${formatDate(suggestion.entryStartDate)} – ${formatDate(suggestion.entryEndDate)}`;
}

function categoryLabel(category: string | null): string {
  return ACTIVITY_CATEGORY_OPTIONS.find((o) => o.value === category)?.label ?? "—";
}

/**
 * The Activity Suggestions data grid — mirrors WidgetsTable.tsx / PtoEntriesTable.tsx.
 * The grid's generic "status" filter slot is repurposed for the AI/Manual
 * source filter (EPIC-007, WI-021) since suggestions have no status field;
 * search covers title + note.
 */
export function ActivitySuggestionsTable() {
  const grid = useDataGrid({ defaultSort: "createdAt" });
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<ActivitySuggestion | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ActivitySuggestion | null>(null);

  const { data, isLoading, isError, error, refetch } = useActivitySuggestions(
    grid.query,
    grid.pageRequest,
  );
  const deleteSuggestion = useDeleteActivitySuggestion();

  const suggestions = data?.data ?? [];

  function handleDeleteConfirm() {
    if (!deleteTarget) return;
    deleteSuggestion.mutate(deleteTarget.id, { onSuccess: () => setDeleteTarget(null) });
  }

  const toolbar = (
    <div className="flex flex-wrap items-center gap-4">
      <SearchInput
        value={grid.searchInput}
        onValueChange={grid.setSearch}
        placeholder="Search suggestions..."
        className="w-full md:flex-1 md:max-w-sm"
      />
      <EnumSelect
        value={grid.status}
        onValueChange={grid.setStatus}
        options={RECOMMENDATION_SOURCE_OPTIONS}
        allOption={{ label: "All sources" }}
        placeholder="All sources"
        className="w-[160px]"
        aria-label="Filter by source"
      />
      <Button size="sm" className="ml-auto" onClick={() => setCreateOpen(true)}>
        <Plus className="h-4 w-4 mr-1" />
        New suggestion
      </Button>
    </div>
  );

  if (isError) {
    return (
      <div className="space-y-4">
        {toolbar}
        <ErrorState
          message={error?.message ?? "Failed to load suggestions"}
          onRetry={() => refetch()}
        />
        <ActivitySuggestionCreateDialog open={createOpen} onOpenChange={setCreateOpen} />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {toolbar}

      <DataTable
        headers={[
          { label: "Title", sortKey: "title" },
          { label: "Source" },
          { label: "Category", className: "hidden lg:table-cell" },
          { label: "Attached entry", className: "hidden md:table-cell" },
          { label: "Note", className: "hidden md:table-cell" },
          { label: "", className: "w-12" },
        ]}
        sort={grid.sort}
        sortDir={grid.sortDir}
        onSort={grid.toggleSort}
        loading={isLoading}
        empty={
          <EmptyState
            icon={Lightbulb}
            title="No suggestions found"
            description="Try adjusting your search, or add a new idea."
          />
        }
      >
        {suggestions.map((suggestion) => (
          <tr
            key={suggestion.id}
            className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors"
          >
            <td className="px-4 py-3 font-medium">{suggestion.title}</td>
            <td className="px-4 py-3">
              <StatusBadge status={suggestion.source} />
            </td>
            <td className="hidden lg:table-cell px-4 py-3 text-muted-foreground">
              {categoryLabel(suggestion.category)}
            </td>
            <td className="hidden md:table-cell px-4 py-3 text-muted-foreground">
              {attachedEntryLabel(suggestion)}
            </td>
            <td className="hidden md:table-cell px-4 py-3 text-muted-foreground max-w-xs truncate">
              {suggestion.note ?? "—"}
            </td>
            <td className="px-4 py-3 text-right">
              <RowActions
                actions={[
                  { label: "Edit", icon: Pencil, onSelect: () => setEditTarget(suggestion) },
                  {
                    label: "Delete",
                    icon: Trash2,
                    destructive: true,
                    onSelect: () => setDeleteTarget(suggestion),
                  },
                ]}
              />
            </td>
          </tr>
        ))}
      </DataTable>

      <CursorPagination
        total={data?.total ?? 0}
        page={grid.page}
        limit={DEFAULT_LIMIT}
        hasNext={data?.hasNext ?? false}
        hasPrev={data?.hasPrev ?? false}
        onNext={() => grid.goNext(data?.nextCursor ?? null)}
        onPrev={() => grid.goPrev(data?.prevCursor ?? null)}
        loading={isLoading}
        itemLabel="suggestions"
      />

      <ActivitySuggestionCreateDialog open={createOpen} onOpenChange={setCreateOpen} />
      <ActivitySuggestionEditDialog
        suggestion={editTarget}
        onOpenChange={(open) => {
          if (!open) setEditTarget(null);
        }}
      />
      <ConfirmDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
        title="Delete suggestion"
        description={`Delete "${deleteTarget?.title ?? "this suggestion"}"?`}
        confirmLabel="Delete"
        variant="destructive"
        loading={deleteSuggestion.isPending}
        onConfirm={handleDeleteConfirm}
      />
    </div>
  );
}
